import { useContext, useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import { AutoTaskAuthContext } from "../../../../Context/AutoTaskContext";
import {
  departmentService,
  readApiError,
} from "../../services/departmentService";
import { ORG_DEPT_SCOPES } from "../../../../utils/departmentTypes";
import { Hint, PrimaryButton, QuietButton, T } from "./ui";

// Creates and edits a department — a row in the same `departments` table the
// ticket screens read, since migration 017 folded the two together.
//
// Only name and head: those are the columns that table has. The code,
// description, status and master-link fields this form used to carry belonged to
// the separate hiring table that 017 removed, and are gone deliberately rather
// than being kept alive in a side table.
const DepartmentFormDrawer = ({
  open,
  onClose,
  department,
  store_id,
  user_id,
  onSaved,
}) => {
  const contextData = useContext(AutoTaskAuthContext) || {};
  const { allUsers } = contextData;

  const activeStaff = (allUsers || []).filter(
    (user) => user.status === "active",
  );

  const isEditMode = !!department;
  const [form, setForm] = useState({ name: "", head: "", type: "org_dept" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setSaving(false);
    setForm({
      name: department?.name || "",
      head: department?.head || department?.dept_head_user_id || "",
      // Org-chart only by default: a department created here should not add
      // itself to everyone's ticket dropdown unless that is asked for.
      type: department?.type || "org_dept",
    });
  }, [department, open]);

  const setField = (field) => (event) =>
    setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setError("Give the department a name");
      return;
    }
    setSaving(true);
    setError("");

    const payload = {
      store_id,
      // The column is 100 chars; trimming here means the server never has to
      // silently truncate what someone typed.
      name: form.name.trim().slice(0, 100),
      // An unset select sends "" from MUI, which is not a valid foreign key.
      head: form.head || null,
      // `org_dept` (org chart only) or `dept_org` (also a ticket department).
      // The API refuses anything else from this module: a plain `dept` row would
      // vanish from the very screen that created it.
      type: form.type || "org_dept",
    };

    try {
      const res = isEditMode
        ? await departmentService.updateDepartment(department.id, payload)
        : await departmentService.createDepartment({
            ...payload,
            created_by: user_id,
          });
      onSaved(res?.data?.data, { created: !isEditMode });
      onClose();
    } catch (err) {
      setError(
        readApiError(
          err,
          isEditMode
            ? "Could not save the department. Try again."
            : "Could not create the department. Try again.",
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={saving ? undefined : onClose}
      PaperProps={{
        sx: { width: { xs: "100%", sm: 440 }, fontFamily: T.font },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 3,
          py: 2.5,
          borderBottom: `1px solid ${T.line}`,
        }}
      >
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: T.radius.md,
            bgcolor: T.accentWash,
            color: T.accent,
            display: "grid",
            placeItems: "center",
            flex: "none",
          }}
        >
          <AccountTreeOutlinedIcon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{ fontFamily: T.font, fontWeight: 700, color: T.ink }}
          >
            {isEditMode ? "Edit department" : "New department"}
          </Typography>
          <Hint sx={{ fontSize: 12.5 }}>
            {isEditMode
              ? form.name
              : "Its teams and roles come next"}
          </Hint>
        </Box>
        <IconButton
          onClick={onClose}
          disabled={saving}
          size="small"
          aria-label="Close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 3,
          py: 3,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
        }}
      >
        {error && (
          <Alert severity="error" sx={{ fontFamily: T.font }}>
            {error}
          </Alert>
        )}

        <TextField
          label="Department name"
          value={form.name}
          onChange={setField("name")}
          fullWidth
          required
          autoFocus
          placeholder="Engineering"
          inputProps={{ maxLength: 100, style: { fontFamily: T.font } }}
          InputLabelProps={{ sx: { fontFamily: T.font } }}
        />

        {/* Org departments and ticket departments are separate lists now. This
            is where a department opts into being both, which is one row on both
            sides rather than two rows that have to be kept in step. */}
        <TextField
          label="Shows up in"
          value={form.type}
          onChange={setField("type")}
          select
          fullWidth
          helperText={
            ORG_DEPT_SCOPES.find((scope) => scope.value === form.type)
              ?.description
          }
          InputLabelProps={{ sx: { fontFamily: T.font } }}
          FormHelperTextProps={{ sx: { fontFamily: T.font } }}
        >
          {ORG_DEPT_SCOPES.map((scope) => (
            <MenuItem key={scope.value} value={scope.value}>
              {scope.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Department head"
          value={form.head}
          onChange={setField("head")}
          select
          fullWidth
          helperText="Optional. Shown on the department card."
          InputLabelProps={{ sx: { fontFamily: T.font } }}
          FormHelperTextProps={{ sx: { fontFamily: T.font } }}
        >
          <MenuItem value="">
            <em>Not set</em>
          </MenuItem>
          {activeStaff.map((staff) => (
            <MenuItem key={staff.id} value={staff.id}>
              {staff.name}
            </MenuItem>
          ))}
        </TextField>

        {!isEditMode && (
          <Box
            sx={{
              bgcolor: T.canvas,
              border: `1px solid ${T.line}`,
              borderRadius: T.radius.md,
              px: 1.75,
              py: 1.5,
            }}
          >
            {/* Said up front rather than discovered later: what this department
                is and is not. It used to be the one shared list, which is why
                the note existed at all. */}
            <Hint sx={{ fontSize: 12.5 }}>
              This is your organization chart. A department here is separate from
              the ticket departments unless you pick “Both” above.
            </Hint>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${T.line}`,
          display: "flex",
          gap: 1.25,
          justifyContent: "flex-end",
        }}
      >
        <QuietButton onClick={onClose} disabled={saving} sx={{ border: "none" }}>
          Cancel
        </QuietButton>
        <PrimaryButton
          onClick={handleSubmit}
          disabled={saving}
          startIcon={
            saving ? <CircularProgress size={15} color="inherit" /> : null
          }
        >
          {isEditMode ? "Save changes" : "Create department"}
        </PrimaryButton>
      </Box>
    </Drawer>
  );
};

export default DepartmentFormDrawer;
